import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const CUSTOM_SORT_QUERY = 'custom_sort';
const CUSTOM_SORT_VALUES = new Set(['discount-desc', 'discount-asc']);

/**
 * @param {string | null} value
 * @returns {value is 'discount-desc' | 'discount-asc'}
 */
const isCustomDiscountSort = (value) => value != null && CUSTOM_SORT_VALUES.has(value);

/**
 * @param {URL} [url]
 * @returns {'discount-desc' | 'discount-asc' | null}
 */
const getCustomSortFromURL = (url = new URL(window.location.href)) => {
  const value = url.searchParams.get(CUSTOM_SORT_QUERY);
  return isCustomDiscountSort(value) ? value : null;
};

/**
 * Sync sort controls to the currently active custom sort value.
 * @param {'discount-desc' | 'discount-asc'} sortValue
 * @param {ParentNode} [root]
 */
const syncCustomSortControls = (sortValue, root = document) => {
  const sortInputs = root.querySelectorAll('input[name="sort_by"]');
  sortInputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    input.checked = input.value === sortValue;
  });

  const selectInputs = root.querySelectorAll('select[name="sort_by"]');
  selectInputs.forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) return;
    const hasOption = Array.from(select.options).some((option) => option.value === sortValue);
    if (hasOption) select.value = sortValue;
  });

  const sortingComponents = root.querySelectorAll('sorting-filter-component');
  sortingComponents.forEach((component) => {
    if (!(component instanceof HTMLElement)) return;
    const options = component.querySelectorAll('[role="option"]');
    options.forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      const radio = option.querySelector('input[name="sort_by"]');
      const isSelected = radio instanceof HTMLInputElement && radio.value === sortValue;
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      option.tabIndex = isSelected ? 0 : -1;
    });
  });
};

/**
 * Reorders product cards by discount percentage.
 * @param {'discount-desc' | 'discount-asc'} sortValue
 * @param {ParentNode} [root]
 */
const applyDiscountSortToGrids = (sortValue, root = document) => {
  const grids = root.querySelectorAll('.product-grid');

  grids.forEach((grid) => {
    if (!(grid instanceof HTMLElement)) return;

    const cards = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
    if (cards.length < 2) return;

    cards.forEach((card, index) => {
      if (!(card instanceof HTMLElement)) return;
      if (!card.dataset.originalPosition) card.dataset.originalPosition = String(index);
    });

    cards.sort((a, b) => {
      if (!(a instanceof HTMLElement) || !(b instanceof HTMLElement)) return 0;

      const aDiscount = Number(a.dataset.discountBps ?? '0');
      const bDiscount = Number(b.dataset.discountBps ?? '0');
      const discountDiff = sortValue === 'discount-desc' ? bDiscount - aDiscount : aDiscount - bDiscount;
      if (discountDiff !== 0) return discountDiff;

      const aPosition = Number(a.dataset.originalPosition ?? '0');
      const bPosition = Number(b.dataset.originalPosition ?? '0');
      return aPosition - bPosition;
    });

    cards.forEach((card) => {
      grid.append(card);
    });
  });
};

/**
 * Restores product cards to their original rendered order.
 * @param {ParentNode} [root]
 */
const restoreOriginalGridOrder = (root = document) => {
  const grids = root.querySelectorAll('.product-grid');

  grids.forEach((grid) => {
    if (!(grid instanceof HTMLElement)) return;

    const cards = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
    if (cards.length < 2) return;

    cards.sort((a, b) => {
      if (!(a instanceof HTMLElement) || !(b instanceof HTMLElement)) return 0;
      const aPosition = Number(a.dataset.originalPosition ?? '0');
      const bPosition = Number(b.dataset.originalPosition ?? '0');
      return aPosition - bPosition;
    });

    cards.forEach((card) => {
      grid.append(card);
    });
  });
};

/**
 * Syncs UI and applies the active custom discount sort from URL.
 * @param {ParentNode} [root]
 */
const applyCustomSortFromURL = (root = document) => {
  const customSort = getCustomSortFromURL();
  if (!customSort) {
    restoreOriginalGridOrder(root);
    return;
  }

  syncCustomSortControls(customSort, root);
  applyDiscountSortToGrids(customSort, root);
};

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // Preserve custom Typesense price params (price_min / price_max) — these are set
    // by history.pushState in typesense-collection.js and are not form inputs, so they
    // would otherwise be silently dropped whenever sort or any other filter changes.
    const currentURL = new URLSearchParams(window.location.search);
    if (currentURL.has('price_min') && !newParameters.has('price_min')) {
      newParameters.set('price_min', currentURL.get('price_min') ?? '');
    }
    if (currentURL.has('price_max') && !newParameters.has('price_max')) {
      newParameters.set('price_max', currentURL.get('price_max') ?? '');
    }

    const selectedSort = newParameters.get('sort_by');
    if (isCustomDiscountSort(selectedSort)) {
      newParameters.delete('sort_by');
      newParameters.set(CUSTOM_SORT_QUERY, selectedSort);
    } else {
      newParameters.delete(CUSTOM_SORT_QUERY);
    }

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    // Clear any facet option search so hidden options are restored after updates.
    resetFacetSearchInputs();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  async #updateSection() {
    const viewTransition = !this.closest('dialog');
    const renderAndApplySort = async () => {
      await sectionRenderer.renderSection(this.sectionId);
      applyCustomSortFromURL();
    };

    if (viewTransition) {
      await startViewTransition(renderAndApplySort, ['product-grid']);
    } else {
      await renderAndApplySort();
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    // Clear any facet option search so hidden options are restored after updates.
    resetFacetSearchInputs();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @param {Event} [event]
   */
  updateFilters(event) {
    const originalTarget = event?.composedPath?.()[0];
    if (ignoreNextFacetUpdateFromSearch) {
      ignoreNextFacetUpdateFromSearch = false;
      if (originalTarget instanceof HTMLInputElement && originalTarget.matches('[data-facet-search]')) {
        return;
      }
    }

    if (originalTarget instanceof HTMLInputElement && originalTarget.matches('[data-facet-search]')) {
      return;
    }
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement | undefined} minRange - The minimum range input
 * @property {HTMLInputElement | undefined} maxRange - The maximum range input
 * @property {HTMLElement | undefined} rangeTrack - The selected range track element
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {HTMLInputElement | undefined} */
  #boundMinRange;

  /** @type {HTMLInputElement | undefined} */
  #boundMaxRange;

  /** @type {HTMLInputElement | undefined} */
  #boundMinInput;

  /** @type {HTMLInputElement | undefined} */
  #boundMaxInput;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#bindRangeEvents();
    this.#syncRangeInputsFromTextInputs();
    this.#setMinAndMaxValues();
    this.#updateRangeTrack();
    requestAnimationFrame(() => this.#updateRangeTrack());
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindRangeEvents();
    this.#syncRangeInputsFromTextInputs();
    this.#setMinAndMaxValues();
    this.#updateRangeTrack();
    requestAnimationFrame(() => this.#updateRangeTrack());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#unbindRangeEvents();
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  #handleRangeInput = () => {
    this.#syncTextInputsFromRangeInputs();
    this.#setMinAndMaxValues();
    this.#updateRangeTrack();
  };

  #handleTextInput = () => {
    this.#syncRangeInputsFromTextInputs();
    this.#setMinAndMaxValues();
    this.#updateRangeTrack();
  };

  #bindRangeEvents() {
    this.#unbindRangeEvents();

    const { minRange, maxRange, minInput, maxInput } = this.refs;
    minRange?.addEventListener('input', this.#handleRangeInput);
    maxRange?.addEventListener('input', this.#handleRangeInput);
    minInput?.addEventListener('input', this.#handleTextInput);
    maxInput?.addEventListener('input', this.#handleTextInput);

    this.#boundMinRange = minRange;
    this.#boundMaxRange = maxRange;
    this.#boundMinInput = minInput;
    this.#boundMaxInput = maxInput;
  }

  #unbindRangeEvents() {
    this.#boundMinRange?.removeEventListener('input', this.#handleRangeInput);
    this.#boundMaxRange?.removeEventListener('input', this.#handleRangeInput);
    this.#boundMinInput?.removeEventListener('input', this.#handleTextInput);
    this.#boundMaxInput?.removeEventListener('input', this.#handleTextInput);

    this.#boundMinRange = undefined;
    this.#boundMaxRange = undefined;
    this.#boundMinInput = undefined;
    this.#boundMaxInput = undefined;
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#syncTextInputsFromRangeInputs();
    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#setMinAndMaxValues();
    this.#syncRangeInputsFromTextInputs();
    this.#updateRangeTrack();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    const value = this.#parseNumber(input.value);
    if (value === null) return;

    const min = this.#parseNumber(input.getAttribute('data-min') ?? '0') ?? 0;
    const max = this.#parseNumber(input.getAttribute('data-max') ?? '') ?? min;

    input.value = Math.min(Math.max(value, min), max).toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;
    const minBound = this.#parseNumber(minInput.getAttribute('data-min') ?? '0') ?? 0;
    const maxBound = this.#parseNumber(maxInput.getAttribute('data-max') ?? '') ?? minBound;
    const minValue = this.#parseNumber(minInput.value);
    const maxValue = this.#parseNumber(maxInput.value);

    minInput.setAttribute('data-max', maxValue === null ? maxBound.toString() : maxValue.toString());
    maxInput.setAttribute('data-min', minValue === null ? minBound.toString() : minValue.toString());
  }

  #syncTextInputsFromRangeInputs() {
    const { minRange, maxRange, minInput, maxInput } = this.refs;
    if (!minRange || !maxRange) return;

    let minValue = this.#parseNumber(minRange.value) ?? 0;
    let maxValue = this.#parseNumber(maxRange.value) ?? 0;

    if (minValue > maxValue) {
      if (document.activeElement === minRange) {
        maxValue = minValue;
      } else {
        minValue = maxValue;
      }
    }

    minRange.value = minValue.toString();
    maxRange.value = maxValue.toString();
    minInput.value = minValue.toString();
    maxInput.value = maxValue.toString();
  }

  #syncRangeInputsFromTextInputs() {
    const { minRange, maxRange, minInput, maxInput } = this.refs;
    if (!minRange || !maxRange) return;

    const rangeMin = this.#parseNumber(minRange.min) ?? 0;
    const rangeMax = this.#parseNumber(maxRange.max) ?? rangeMin;
    const minInputValue = this.#parseNumber(minInput.value);
    const maxInputValue = this.#parseNumber(maxInput.value);

    let minValue = minInputValue === null ? rangeMin : Math.min(Math.max(minInputValue, rangeMin), rangeMax);
    let maxValue = maxInputValue === null ? rangeMax : Math.min(Math.max(maxInputValue, rangeMin), rangeMax);

    if (minValue > maxValue) {
      if (document.activeElement === maxInput) {
        minValue = maxValue;
      } else {
        maxValue = minValue;
      }
    }

    minRange.value = minValue.toString();
    maxRange.value = maxValue.toString();
  }

  #updateRangeTrack() {
    const { minRange, maxRange, rangeTrack } = this.refs;
    if (!minRange || !maxRange || !rangeTrack) return;

    const minBound = this.#parseNumber(minRange.min) ?? 0;
    const maxBound = this.#parseNumber(maxRange.max) ?? minBound;
    const minValue = this.#parseNumber(minRange.value) ?? minBound;
    const maxValue = this.#parseNumber(maxRange.value) ?? maxBound;
    const span = maxBound - minBound;
    if (span <= 0) return;

    const minPercent = ((minValue - minBound) / span) * 100;
    const maxPercent = ((maxValue - minBound) / span) * 100;

    rangeTrack.style.left = `calc(${minPercent}% + 8px)`;
    rangeTrack.style.right = `calc(${100 - maxPercent}% + 8px)`;
  }

  /**
   * Parses decimal input into a number
   * @param {string} value
   * @returns {number | null}
   */
  #parseNumber(value) {
    if (value.trim() === '') return null;
    const normalized = formatMoney(value.replace(/\s+/g, '').replace(/'/g, ''));
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const hasFilterParams = [...parsedUrl.searchParams.keys()].some((key) => key.startsWith('filter.'));

      // When the "Clear all" URL has no filter params, reset any facet option search
      // so previously hidden options become visible again after the morph.
      if (!hasFilterParams) {
        resetFacetSearchInputs();
      }
    } catch (error) {
      // If the URL cannot be parsed, continue with the existing behavior.
    }

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * @param {HTMLElement | null | undefined} element
 */
function focusWithoutScroll(element) {
  if (!element) return;

  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        focusWithoutScroll(selectedOption);
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      focusWithoutScroll(targetOption);
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        focusWithoutScroll(summary);
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const target = event.target;
    const selectedSortValue =
      target instanceof HTMLInputElement || target instanceof HTMLSelectElement ? target.value : null;

    if (isCustomDiscountSort(selectedSortValue)) {
      const url = new URL(window.location.href);
      url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
      url.searchParams.set(CUSTOM_SORT_QUERY, selectedSortValue);
      history.pushState({ urlParameters: url.searchParams.toString() }, '', url.toString());

      syncCustomSortControls(selectedSortValue);
      applyDiscountSortToGrids(selectedSortValue);
      this.updateFacetStatus(event);

      const { details } = this.refs;
      if (details instanceof HTMLDetailsElement) {
        details.open = false;
      }
      return;
    }

    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    const { target } = event;
    if (!(target instanceof HTMLSelectElement) && !(target instanceof HTMLInputElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    let optionName = target.dataset.optionName ?? '';
    if (target instanceof HTMLSelectElement && optionName === '') {
      optionName = target.selectedOptions[0]?.textContent?.trim() ?? '';
    }

    facetStatus.textContent =
      target.value !== details.dataset.defaultSortBy ? optionName : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

document.addEventListener('DOMContentLoaded', () => {
  applyCustomSortFromURL();
});

window.addEventListener('popstate', () => {
  applyCustomSortFromURL();
});

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

const FACET_SEARCH_HIDDEN_CLASSES = ['hidden', 'mobile:hidden'];

/**
 * @param {HTMLInputElement} input
 * @returns {HTMLElement | null}
 */
const getFacetSearchContainer = (input) =>
  input.closest('.mobile-filters__facet-panel') ||
  input.closest('.facets__panel-content') ||
  input.closest('.facets__panel');

/**
 * @param {HTMLElement} item
 * @returns {void}
 */
const storeHiddenClasses = (item) => {
  if (item.dataset.searchHiddenClasses) return;
  const classes = FACET_SEARCH_HIDDEN_CLASSES.filter((className) => item.classList.contains(className));
  if (classes.length) {
    item.dataset.searchHiddenClasses = classes.join(' ');
  }
};

/**
 * @param {HTMLElement} item
 * @returns {void}
 */
const restoreHiddenClasses = (item) => {
  if (!item.dataset.searchHiddenClasses) return;
  item.dataset.searchHiddenClasses
    .split(' ')
    .filter(Boolean)
    .forEach((className) => item.classList.add(className));
  delete item.dataset.searchHiddenClasses;
};

/**
 * @param {HTMLInputElement} input
 * @returns {void}
 */
const filterFacetOptions = (input) => {
  const container = getFacetSearchContainer(input);
  if (!container) return;

  const query = input.value.trim().toLowerCase();
  const items = container.querySelectorAll('[data-value-label]');

  items.forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    const label = (item.dataset.valueLabel || '').toLowerCase();

    if (query) {
      storeHiddenClasses(item);
      FACET_SEARCH_HIDDEN_CLASSES.forEach((className) => item.classList.remove(className));
    } else {
      restoreHiddenClasses(item);
    }

    const shouldShow = query === '' || label.includes(query);
    item.toggleAttribute('hidden', !shouldShow);
  });
};

/**
 * Resets all facet search inputs and restores option visibility.
 * @param {ParentNode} [root=document]
 */
function resetFacetSearchInputs(root = document) {
  const inputs = root.querySelectorAll('input[data-facet-search]');
  inputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    if (input.value !== '') input.value = '';
    filterFacetOptions(input);
  });
}

let ignoreNextFacetUpdateFromSearch = false;

document.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches('[data-facet-search]')) return;
  filterFacetOptions(target);
});

// Prevent the facet search input from triggering filter updates on blur.
// The facet container listens to `change` events and would otherwise re-render
// before the first checkbox click is applied.
document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-facet-search]')) return;
    ignoreNextFacetUpdateFromSearch = true;
    event.stopPropagation();
  },
  true
);
